import React, { useState } from 'react';
import PropTypes from 'prop-types';

import AddControl from 'components/add_control';
import Breadcrumbs from './breadcrumbs';
import Dropzone from 'components/dropzone';
import FileSystem from 'components/file_system';
import Placeholder from './placeholder';
import Table from './table';
import { createAttachment, createFolder } from './api';
import { rootFolder } from './config';

// A wrapper around FileSystem components for attachments and attachment folders
export default function AttachmentsFileSystem({
  attachable,
  attachments,
  canDeleteOwnAttachments = true,
  canManageAllAttachments = false,
  folders,
  forUser,
  noPreviewComponent,
  preview,
  onAttachmentClick,
  onAttachmentCreated,
  onAttachmentsChange,
  onFoldersChange
}) {
  const [currentFolder, setCurrentFolder] = useState(rootFolder);
  const [previewFile, setPreviewFile] = useState(null);
  async function createNewFolder() {
    const folder = await createFolder(attachable.attachmentFoldersPath, 'New Folder', currentFolder.id);
    onFoldersChange([folder, ...folders]);
  }

  if (preview && previewFile) {
    return (
      <FileSystem.Preview
        file={previewFile}
        noPreviewComponent={noPreviewComponent}
        onExit={() => setPreviewFile(null)}
      />
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      <Dropzone
        placeholder={<Placeholder />}
        upload={(file, config) => createAttachment(attachable, file, config, currentFolder)}
        onSuccess={onAttachmentCreated}
      />

      <div className="d-flex justify-content-between align-items-center gap-3">
        <Breadcrumbs
          currentFolder={currentFolder}
          folders={folders}
          onFolderClick={setCurrentFolder}
        />

        <AddControl aria-label="addFolder" onClick={createNewFolder}>
          Add folder
        </AddControl>
      </div>

      <Table
        attachments={attachments}
        canDeleteOwnAttachments={canDeleteOwnAttachments}
        canManageAllAttachments={canManageAllAttachments}
        currentFolder={currentFolder}
        folders={folders}
        forUser={forUser}
        onAttachmentsChange={onAttachmentsChange}
        onFileClick={preview ? setPreviewFile : onAttachmentClick}
        onFolderClick={setCurrentFolder}
        onFoldersChange={onFoldersChange}
      />
    </div>
  );
}

AttachmentsFileSystem.propTypes = {
  attachable: PropTypes.shape({
    attachmentFoldersPath: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  attachments: PropTypes.array,
  canDeleteOwnAttachments: PropTypes.bool,
  canManageAllAttachments: PropTypes.bool,
  folders: PropTypes.array,
  forUser: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  noPreviewComponent: PropTypes.node,
  preview: PropTypes.bool,
  onAttachmentClick: PropTypes.func,
  onAttachmentCreated: PropTypes.func,
  onAttachmentsChange: PropTypes.func,
  onFoldersChange: PropTypes.func,
};
